import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { CampaignFilters, LeadStatus } from '@fenix/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { campaignsApi, leadsApi, scriptsApi, settingsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { LEAD_STATUS_LABEL, fmtPhone } from '@/lib/format';

const ANY = '__any__';
const STATUS_OPTIONS: LeadStatus[] = ['new', 'contacted', 'failed', 'not_interested'];

export function NewCampaignDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const settings = useQuery({ queryKey: ['settings'], queryFn: settingsApi.get, enabled: open });
  const facets = useQuery({
    queryKey: ['leads', 'facets'],
    queryFn: leadsApi.facets,
    enabled: open,
  });
  const languages = useQuery({
    queryKey: ['languages'],
    queryFn: settingsApi.languages,
    enabled: open,
  });

  const [name, setName] = useState('');
  const [language, setLanguage] = useState<string>();
  const [templateId, setTemplateId] = useState<string>();
  const [filters, setFilters] = useState<CampaignFilters>({ statuses: ['new'] });
  const lang = language ?? settings.data?.callLanguage;
  const scripts = useQuery({
    queryKey: ['scripts', lang],
    queryFn: () => scriptsApi.list(lang),
    enabled: open && !!lang,
  });
  const preview = useQuery({
    queryKey: ['campaigns', 'preview', filters],
    queryFn: () => campaignsApi.preview(filters),
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setName(`Recuperação ${new Date().toLocaleDateString('pt-BR')}`);
      setFilters({ statuses: ['new'] });
      setTemplateId(undefined);
      setLanguage(undefined);
    }
  }, [open]);

  const mut = useMutation({
    mutationFn: () =>
      campaignsApi.create({
        name,
        language: lang as never,
        scriptTemplateId: templateId ?? scripts.data?.find((s) => s.isDefault)?.id,
        filters,
      }),
    onSuccess: (c) => {
      toast.success('Campanha criada');
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
      onOpenChange(false);
      navigate(`/campanhas/${c.id}`);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro ao criar'),
  });

  const toggleStatus = (s: LeadStatus, on: boolean) =>
    setFilters((f) => ({
      ...f,
      statuses: on ? [...(f.statuses ?? []), s] : (f.statuses ?? []).filter((x) => x !== s),
    }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Nova campanha</DialogTitle>
          <DialogDescription>
            Escolha os leads por filtro; a discagem respeita a janela de horário, tentativas e
            limites das Configurações.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Nome</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="min-w-0 space-y-1.5">
            <Label>Idioma</Label>
            <Select
              value={lang}
              onValueChange={(v) => {
                setLanguage(v);
                setTemplateId(undefined);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.data?.map((l) => (
                  <SelectItem key={l.code} value={l.code}>
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-0 space-y-1.5">
            <Label>Script</Label>
            <Select
              value={templateId ?? scripts.data?.find((s) => s.isDefault)?.id}
              onValueChange={setTemplateId}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Padrão" />
              </SelectTrigger>
              <SelectContent>
                {scripts.data?.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-0 space-y-1.5">
            <Label>Origem</Label>
            <Select
              value={filters.sourceSlug ?? ANY}
              onValueChange={(v) =>
                setFilters((f) => ({ ...f, sourceSlug: v === ANY ? undefined : v }))
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Qualquer</SelectItem>
                {facets.data?.sources.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.value} ({s.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-0 space-y-1.5">
            <Label>Produto</Label>
            <Select
              value={filters.productCodename ?? ANY}
              onValueChange={(v) =>
                setFilters((f) => ({ ...f, productCodename: v === ANY ? undefined : v }))
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Qualquer</SelectItem>
                {facets.data?.products.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.value} ({s.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-0 space-y-1.5">
            <Label>País</Label>
            <Select
              value={filters.country ?? ANY}
              onValueChange={(v) =>
                setFilters((f) => ({ ...f, country: v === ANY ? undefined : v }))
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Qualquer</SelectItem>
                {facets.data?.countries.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.value} ({s.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-0 space-y-1.5">
            <Label>Status dos leads</Label>
            <div className="grid grid-cols-2 gap-1.5 pt-1">
              {STATUS_OPTIONS.map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={filters.statuses?.includes(s)}
                    onCheckedChange={(v) => toggleStatus(s, v === true)}
                  />{' '}
                  {LEAD_STATUS_LABEL[s]}
                </label>
              ))}
            </div>
          </div>
          <div className="rounded-lg border bg-muted/40 p-3 text-sm sm:col-span-2">
            <p className="font-medium">
              {preview.isFetching
                ? 'Calculando…'
                : `${preview.data?.count ?? 0} lead(s) serão incluídos`}
            </p>
            {preview.data?.sample.length ? (
              <ul className="mt-1 text-xs text-muted-foreground">
                {preview.data.sample.map((l) => (
                  <li key={l.id}>
                    {l.name || 'Sem nome'} · {fmtPhone(l.phoneE164)} · {l.productCodename}
                  </li>
                ))}
                {preview.data.count > preview.data.sample.length && (
                  <li>… e mais {preview.data.count - preview.data.sample.length}</li>
                )}
              </ul>
            ) : null}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => mut.mutate()}
            disabled={mut.isPending || !name || !preview.data?.count}
          >
            {mut.isPending && <Loader2 className="animate-spin" />} Criar campanha
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
